import { DependencyList, useEffect, useRef, useState } from 'react';
import { JC_LOADING, JcLoading, isLoading } from '../utils/JcLoading';
import { JcUnsubscribe } from '../utils/JcUnsubscribe';
import { JcDataError } from './JcDataError';
import { JcAsyncResult } from './JcAsyncResult';

/** The load-more controls of a {@link JcPagedResult}, threadable as one prop. */
export interface JcPagedControls {
    /** More items exist beyond the current window. */
    readonly hasMore: boolean;
    /** A `loadMore` grow is in flight. */
    readonly isLoadingMore: boolean;
    /** Grows the window by one page; no-op while loading or when nothing more exists. */
    readonly loadMore: () => void;
}

/** A list result whose window grows a page at a time — feed it to `JcLoadMore`. */
export interface JcPagedResult<T> extends JcAsyncResult<T[]>, JcPagedControls {}

export interface JcPagedSnapshotOptions {
    /** Items in the initial window and in each `loadMore` grow. */
    readonly pageSize: number;
}

/**
 * Paged variant of `useJcSnapshot`: one live subscription whose item limit
 * grows by `pageSize` on each `loadMore`. The previous window stays rendered
 * while a grow re-subscribes — the value resets to `JC_LOADING` (and the
 * limit to one page) only when `deps` change or on `retry`. The `subscribe`
 * closure reports `hasMore` alongside the items (see the service-side N+1
 * probe pattern in docs/services.md).
 */
export const useJcPagedSnapshot = <T>(
    subscribe: (
        limit: number,
        onValue: (items: T[], hasMore: boolean) => void,
        onError: (error: JcDataError) => void,
    ) => JcUnsubscribe,
    deps: DependencyList,
    options: JcPagedSnapshotOptions,
): JcPagedResult<T> => {
    const { pageSize } = options;
    const [value, setValue] = useState<T[] | JcLoading>(JC_LOADING);
    const [error, setError] = useState<JcDataError | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [limit, setLimit] = useState(pageSize);
    const [nonce, setNonce] = useState(0);
    // The subscription's identity (deps + retry nonce). A limit grow keeps the
    // identity, so the current window stays rendered across the re-subscribe.
    const identityRef = useRef<readonly unknown[] | null>(null);

    useEffect(() => {
        const identity = [...deps, nonce];
        const previous = identityRef.current;
        const identityChanged = previous === null
            || previous.length !== identity.length
            || identity.some((dep, i) => !Object.is(dep, previous[i]));
        identityRef.current = identity;
        if (identityChanged) {
            setValue(JC_LOADING);
            setError(null);
            setHasMore(false);
            setIsLoadingMore(false);
            if (limit !== pageSize) {
                // Snap back to one page; the effect re-runs to subscribe there.
                setLimit(pageSize);
                return;
            }
        }
        return subscribe(
            limit,
            (items, more) => {
                setValue(items);
                setHasMore(more);
                setIsLoadingMore(false);
            },
            (dataError) => {
                setError(dataError);
                setIsLoadingMore(false);
            },
        );
        // `subscribe` is tracked via `deps`; `nonce` forces a re-subscribe on retry.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, nonce, limit]);

    const loadMore = (): void => {
        if (!hasMore || isLoadingMore || isLoading(value)) {
            return;
        }
        setIsLoadingMore(true);
        setLimit((current) => current + pageSize);
    };

    return { value, error, retry: () => setNonce((n) => n + 1), hasMore, isLoadingMore, loadMore };
};
